import { useCallback, useEffect, useRef, useState } from 'react';

// Module-level registry to ensure only one popup stays open at a time.
const popupRegistry = new Map();
let nextPopupId = 1;

const closeOtherPopups = (currentId) => {
    popupRegistry.forEach((closeFn, id) => {
        if (id !== currentId) closeFn();
    });
};

/**
 * usePopup
 *
 * Provides popup state, refs, and positioning relative to an anchor element.
 *
 * @param {object} options - Hook options.
 * @param {number} options.offset - Vertical offset for popup positioning.
 * @param {number} options.closeDelay - Delay before hiding popup after close.
 * @param {string} options.align - Horizontal alignment relative to anchor: left|right|auto.
 * @param {string} options.placement - Vertical placement relative to anchor: bottom|top|auto.
 * @param {number} options.viewportPadding - Minimum padding from viewport edges.
 * @param {object} options.anchorRef - Optional external ref for the anchor element.
 * @param {{x: number, y: number}} options.anchorPoint - Optional viewport point anchor.
 * @param {boolean} options.isOpen - Optional controlled open state.
 * @param {function} options.setOpen - Optional controlled setter for open state.
 *
 * @returns {object} - Popup state, refs, and actions.
 */
const usePopup = ({
    offset = 2,
    closeDelay = 300,
    align = 'left',
    placement = 'bottom',
    viewportPadding = 8,
    anchorRef: externalAnchorRef,
    anchorPoint,
    isOpen: controlledIsOpen,
    setOpen: setOpenProp,
} = {}) => {
    
    // ----- STATE ----- //

    // Popup State
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const setOpen = useCallback((next) => {
        if (typeof setOpenProp === 'function') {
            setOpenProp(next);
        } else {
            setInternalIsOpen(next);
        }
    }, [setOpenProp]);

    const isOpen = typeof controlledIsOpen === 'boolean' ? controlledIsOpen : internalIsOpen;

    // Refs
    const internalAnchorRef = useRef(null);
    const anchorRef = externalAnchorRef || internalAnchorRef;
    const popupRef = useRef(null);
    const popupIdRef = useRef(null);

    if (!popupIdRef.current) {
        popupIdRef.current = nextPopupId++;
    }

    // ----- UTILITIES ----- //

    /**
     * Updates the popup position based on the anchor element's position.
     */
    const updatePosition = useCallback(() => {
        const hasAnchorRef = Boolean(anchorRef.current);
        const hasAnchorPoint = Number.isFinite(anchorPoint?.x) && Number.isFinite(anchorPoint?.y);
        if (!hasAnchorRef && !hasAnchorPoint) return;

        const rect = hasAnchorRef
            ? anchorRef.current.getBoundingClientRect()
            : {
                left: anchorPoint.x,
                right: anchorPoint.x,
                top: anchorPoint.y,
                bottom: anchorPoint.y,
            };
        const popupRect = popupRef.current?.getBoundingClientRect();
        const popupWidth = popupRect?.width || 0;
        const popupHeight = popupRect?.height || 0;
        const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
        const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;

        let left = rect.left;
        if (align === 'right') {
            left = rect.right - popupWidth;
        } else if (align === 'auto' && popupWidth && viewportWidth) {
            const leftCandidate = rect.left;
            const rightCandidate = rect.right - popupWidth;
            const overflowsRight = leftCandidate + popupWidth > viewportWidth - viewportPadding;
            const fitsRight = rightCandidate >= viewportPadding;
            left = overflowsRight && fitsRight ? rightCandidate : leftCandidate;
        }

        if (viewportWidth && popupWidth) {
            const minLeft = viewportPadding;
            const maxLeft = Math.max(viewportPadding, viewportWidth - popupWidth - viewportPadding);
            left = Math.min(Math.max(left, minLeft), maxLeft);
        }

        let top = rect.bottom + offset;
        if (placement === 'top') {
            top = rect.top - popupHeight - offset;
        } else if (placement === 'auto' && popupHeight && viewportHeight) {
            const spaceBelow = viewportHeight - rect.bottom - viewportPadding;
            const spaceAbove = rect.top - viewportPadding;
            const shouldFlip = spaceBelow < popupHeight && spaceAbove > spaceBelow;
            top = shouldFlip ? rect.top - popupHeight - offset : rect.bottom + offset;
        }

        setPosition({ top, left });
    }, [offset, align, placement, viewportPadding, anchorPoint, anchorRef]);

    // ----- EFFECTS ----- //

    /**
     * Effect to update popup position on open, window resize, and scroll.
     */
    useEffect(() => {
        // Only add listeners if popup is open
        if (!isOpen) return;

        // Initial position update
        updatePosition();

        // Event listeners
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        // Cleanup
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };

    }, [isOpen, updatePosition]);

    /**
     * Effect to handle popup visibility with a fade-out effect.
     */
    useEffect(() => {
        let timeoutId;
        if (isOpen) {
            setIsVisible(true);
        } else if (isVisible) {
            timeoutId = setTimeout(() => setIsVisible(false), closeDelay);
        }
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isOpen, isVisible, closeDelay]);

    /**
     * Effect to re-run positioning once the popup is mounted and visible.
     */
    useEffect(() => {
        if (!isVisible) return;
        const frameId = requestAnimationFrame(updatePosition);
        return () => cancelAnimationFrame(frameId);
    }, [isVisible, updatePosition]);

    /**
     * Effect to handle clicks outside the anchor and popup to close the popup.
     */
    useEffect(() => {

        // Only add listener if popup is open
        if (!isOpen) return;

        // Pointer down handler
        const handlePointerDown = (event) => {

            // Check if click is inside anchor or popup
            const target = event.target;

            // If inside, do nothing
            if (anchorRef.current && anchorRef.current.contains(target)) return;
            if (popupRef.current && popupRef.current.contains(target)) return;

            // Otherwise, close popup
            setOpen(false);

        };

        // Add event listener
        document.addEventListener('mousedown', handlePointerDown);

        // Cleanup
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
        };

    }, [anchorRef, isOpen, setOpen]);

    // ----- ACTIONS ----- //

    const open = useCallback(() => {
        closeOtherPopups(popupIdRef.current);
        setOpen(true);
        requestAnimationFrame(updatePosition);
    }, [setOpen, updatePosition]);

    const close = useCallback(() => setOpen(false), [setOpen]);

    const toggle = useCallback(() => {
        setOpen((prev) => {
            const next = !prev;
            if (!prev) {
                closeOtherPopups(popupIdRef.current);
                requestAnimationFrame(updatePosition);
            }
            return next;
        });
    }, [setOpen, updatePosition]);

    // Register this popup so other popups can close it when they open.
    useEffect(() => {
        popupRegistry.set(popupIdRef.current, close);
        return () => {
            popupRegistry.delete(popupIdRef.current);
        };
    }, [close]);

    // If a controlled open happens externally, close any other popups.
    useEffect(() => {
        if (isOpen) {
            closeOtherPopups(popupIdRef.current);
        }
    }, [isOpen]);

    return {
        anchorRef,
        popupRef,
        isOpen,
        isVisible,
        position,
        open,
        close,
        toggle,
    };
};

export default usePopup;
